// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // During SSR there is no window. Public URLs (webmanifest etc.) pass
  // marketplaceRootURL and must keep it; plain API calls from loadData use
  // the loopback so catalog data reaches the server-rendered HTML.
  // NOTE: webpack statically inlines `process.env.PORT` at build time (as
  // undefined), so the real runtime port must be read with a bracket lookup
  // that the bundler can't replace. 127.0.0.1 (not "localhost") avoids the
  // IPv6 resolution trap on newer Node.
  if (typeof window === 'undefined') {
    if (marketplaceRootURL) {
      return marketplaceRootURL.replace(/\/$/, '');
    }
    const runtimeEnv = typeof process !== 'undefined' && process.env ? process.env : {};
    const runtimePort = runtimeEnv['PORT'];
    if (runtimePort) {
      return `http://127.0.0.1:${runtimePort}`;
    }
    // Last resort: the public root URL (inlined at build time) or dev port.
    return process.env.REACT_APP_MARKETPLACE_ROOT_URL
      ? process.env.REACT_APP_MARKETPLACE_ROOT_URL.replace(/\/$/, '')
      : `http://127.0.0.1:4000`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // window.fetch in browsers, global fetch (Node 18+) during SSR.
  const fetchFn = typeof window !== 'undefined' ? window.fetch : fetch;
  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const checkInquiryRate = body => {
  return post('/api/inquiry-rate', body);
};

// Firestore-backed catalog (migration off Sharetribe)
export const getCatalogProducts = (queryParams = {}) => {
  const qs = new URLSearchParams(queryParams).toString();
  return get(`/api/catalog/products${qs ? `?${qs}` : ''}`);
};

export const getCatalogProduct = id => {
  return get(`/api/catalog/products/${encodeURIComponent(id)}`);
};

export const getCatalogShops = () => {
  return get('/api/catalog/shops');
};

export const getCatalogShop = id => {
  return get(`/api/catalog/shops/${encodeURIComponent(id)}`);
};

export const postCatalogInquiry = (body, customerIdToken) => {
  const options = customerIdToken
    ? {
        headers: {
          'Content-Type': 'application/transit+json',
          Authorization: `Bearer ${customerIdToken}`,
        },
      }
    : {};
  return post('/api/catalog/inquiry', body, options);
};

// Fire-and-forget: tell the server an address was entered/checked on a product
// card, so the operator gets a Telegram ping (valid or rejected). Never throws.
export const postCatalogAddressAttempt = body =>
  post('/api/catalog/address-attempt', body).catch(() => {});

// True when this build should read catalog data from Firestore instead of
// the Sharetribe Marketplace API (staging/после переключения).
export const useFirebaseCatalog = () => process.env.REACT_APP_CATALOG_SOURCE === 'firebase';

// Sharetribe-shaped listings search served from Firestore (transit response
// with real Money/UUID types, safe for addMarketplaceEntities).
export const catalogSearchListings = (queryParams = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const qs = new URLSearchParams(cleaned).toString();
  return get(`/api/catalog/search${qs ? `?${qs}` : ''}`).then(payload => ({ data: payload }));
};

export const getAdminInquiries = adminToken => {
  return get('/api/catalog-admin/inquiries', {
    headers: { 'x-admin-token': adminToken },
  });
};

export const updateAdminInquiryStatus = (id, status, adminToken) => {
  return post(
    `/api/catalog-admin/inquiries/${encodeURIComponent(id)}/status`,
    { status },
    { headers: { 'Content-Type': 'application/transit+json', 'x-admin-token': adminToken } }
  );
};

//mux endpoints
export const getMuxUploadUrl = () => {
  return get('/api/mux/upload-url');
};

export const getMuxAsset = (queryParams = {}) => {
  return get('/api/mux/get-asset?' + new URLSearchParams(queryParams).toString());
};
